/**
 * TranscriptModal — shows a full call transcript in a scrollable modal dialog.
 *
 * Long call transcripts used to expand inline inside the call-event card in
 * CommsThread, which stretched the conversation thread out of view. This modal
 * hosts the complete transcript instead: the call card shows only a short
 * preview + a "View full transcript" button, and the full segment list scrolls
 * within the modal body (forms/Modal.tsx — max-h-[90vh] panel, overflow-y-auto
 * body) so it never bloats the parent thread.
 *
 * Speaker attribution (Twilio track swap by direction), speaker naming,
 * Recruiter/Patient bubble styling, and PHI masking are ported VERBATIM from
 * the old inline rendering in CommsThread so behavior is identical.
 */
import Modal from './forms/Modal';
import { formatPhone } from '../lib';

interface TranscriptModalProps {
  isOpen: boolean;
  onClose: () => void;
  transcript: { track?: string; text?: string }[];
  direction: string | null;        // 'inbound' | 'outbound' | null
  contactName?: string;
  contactPhone?: string | null;
  recruiter?: string | null;       // resolved recruiter display name, or null
  mask?: boolean;
}

export default function TranscriptModal({
  isOpen,
  onClose,
  transcript,
  direction,
  contactName,
  contactPhone,
  recruiter,
  mask = false,
}: TranscriptModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Call transcript (${transcript.length})`} maxWidth="max-w-2xl">
      <div className="space-y-1.5">
        {transcript.map((seg, i) => {
          // Twilio's track names are relative to the call leg the
          // transcription TwiML ran on: the patient's leg for inbound calls,
          // the recruiter's browser leg for outbound calls. So outbound_track
          // is the recruiter on inbound calls but the PATIENT on outbound calls.
          const rec = direction === 'inbound'
            ? seg.track === 'outbound_track'
            : seg.track === 'inbound_track';
          // Name the speakers: the recruiter who took the call, and this
          // contact (name, else their number, else 'Patient').
          const speaker = rec
            ? (recruiter || 'Recruiter')
            : (contactName || (contactPhone ? formatPhone(contactPhone) : null) || 'Patient');
          return (
            <div key={i} className={`flex ${rec ? 'justify-end' : 'justify-start'}`}>
              <p
                className={`max-w-[85%] rounded-lg px-2 py-1 text-xs leading-relaxed ${
                  rec
                    ? 'bg-primary-600/10 text-primary-900 dark:bg-primary-400/10 dark:text-primary-100'
                    : 'border border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                <span className={`font-medium ${rec ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {speaker}:
                </span>{' '}
                {mask ? '•••••' : seg.text}
              </p>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
